import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, CheckCircle2, TrendingUp, Zap, BarChart3, Target, Users, Lightbulb, ArrowLeft } from 'lucide-react';

export default function AiMarketingAutomationArticle() {
  const navigate = useNavigate();
  const [visibleSections, setVisibleSections] = useState(new Set());
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  return (
    <div className="bg-white text-zinc-900">
      {/* Back Button */}
      <div className="bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-4">
          <Link to="/resources/articles" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold transition-colors text-sm">
            <ArrowLeft size={16} />
            Back to Articles
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <article className="max-w-4xl mx-auto px-6 lg:px-8 py-12">
        <div data-animate="hero" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-block px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold tracking-wider mb-4 rounded">
            MARKETING AUTOMATION
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight text-zinc-900">
            How AI-Powered Marketing Automation Saves 20+ Hours Per Week
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 mb-8 pb-8 border-b border-zinc-200">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-zinc-400" />
              <span>28 December 2024</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              <span>8 min read</span>
            </div>
            <div>
              <span className="font-medium">By Digital Trent Team</span>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <section data-animate="intro" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('intro') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="prose prose-lg max-w-none text-zinc-700">
            <p className="text-lg leading-relaxed mb-6">
              If you're a small business owner or marketing manager, you know the pain: <strong className="text-zinc-900">20+ hours every week</strong> spent on repetitive marketing tasks that never seem to end. Creating content calendars, scheduling social posts, writing emails, following up with leads, tracking campaign performance—it's exhausting.
            </p>
            <p className="leading-relaxed mb-6">
              The harsh reality? Whilst you're buried in these manual tasks, your competitors using AI-powered automation are scaling faster, engaging more customers, and actually having time to focus on strategy and growth.
            </p>
            <p className="leading-relaxed">
              But here's the good news: <strong className="text-zinc-900">intelligent automation isn't just for enterprises any more.</strong> In this comprehensive guide, we'll show you exactly how AI-powered marketing automation saves 20+ hours per week and delivers better results than manual marketing ever could.
            </p>
          </div>
        </section>

        {/* The Time Sink Reality */}
        <section data-animate="problem" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('problem') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp size={22} className="text-white" />
            </div>
            The Marketing Time Sink: Where Your Hours Actually Go
          </h2>

          <div className="bg-red-50 border-l-4 border-red-500 p-8 mb-8 rounded-r-lg">
            <p className="text-zinc-700 font-semibold mb-5">Let's be brutally honest about where your marketing time disappears:</p>
            <ul className="space-y-3 text-zinc-600">
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Content Creation:</strong> 6-8 hours/week writing social posts, blogs, and emails</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Manual Scheduling:</strong> 3-4 hours/week posting across platforms</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Lead Follow-ups:</strong> 4-5 hours/week sending individual emails</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Campaign Tracking:</strong> 2-3 hours/week compiling reports from multiple tools</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1 text-lg">•</span>
                <span><strong className="text-zinc-900">Strategy Adjustments:</strong> 3-4 hours/week analysing and tweaking</span>
              </li>
            </ul>
            <p className="text-zinc-900 font-bold mt-6 text-lg">Total: 20-25 hours per week on tasks that could be automated.</p>
          </div>

          <p className="text-zinc-700 leading-relaxed text-lg">
            And here's the kicker: <strong className="text-zinc-900">these manual processes are costing you opportunities.</strong> Whilst you're scheduling Tuesday's social post, a hot lead from Monday is going cold. Whilst you're creating next week's email, your competitor's AI just sent 47 personalised follow-ups.
          </p>
        </section>

        {/* How AI Automation Works */}
        <section data-animate="solution" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('solution') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Zap size={22} className="text-white" />
            </div>
            How AI-Powered Automation Actually Works
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            Modern AI marketing automation isn't about replacing you—it's about amplifying your impact. Here's what intelligent automation handles automatically:
          </p>

          <div className="space-y-6 mb-8">
            {[
              {
                num: '1',
                title: 'AI Strategy Development',
                time: '15 minutes vs. 8 hours',
                desc: 'Instead of spending a full day creating a marketing strategy, AI analyses your business, competitors, and market positioning to generate a comprehensive 120-day plan in 15 minutes.',
                color: 'blue',
                items: ['SMART goals with measurable KPIs', 'Market analysis and competitive positioning', 'Channel-specific strategies (social, email, content)', '90-day execution roadmap']
              },
              {
                num: '2',
                title: 'Automated Content Generation',
                time: '10 min vs. 6-8 hours/week',
                desc: 'AI generates 480+ platform-optimised posts, emails, and captions aligned with your brand voice and strategy—all ready to review and schedule.',
                color: 'green',
                items: ['Instagram posts with hashtags and captions', 'LinkedIn thought leadership content', 'Facebook engagement posts', 'Email campaigns with subject lines', 'Blog article outlines and drafts']
              },
              {
                num: '3',
                title: 'Smart Scheduling & Publishing',
                time: 'Automatic vs. 3-4 hours/week',
                desc: 'Set your preferences once, and the system automatically posts at optimal times across all platforms—no more logging into five different tools every day.',
                color: 'purple',
                items: ['Best time posting based on audience engagement', 'Multi-platform distribution from one dashboard', 'Queue management and content spacing', 'Holiday and campaign scheduling']
              },
              {
                num: '4',
                title: 'Intelligent Lead Nurturing',
                time: 'Automatic vs. 4-5 hours/week',
                desc: 'AI tracks every customer interaction and automatically sends personalised follow-ups, reminders, and offers based on behaviour and engagement.',
                color: 'orange',
                items: ['Welcome sequences for new leads', 'Behavioural triggers (website visits, email opens)', 'Abandoned cart recovery', 'Re-engagement campaigns for cold leads', 'Personalised product recommendations']
              },
              {
                num: '5',
                title: 'Real-Time Analytics & Optimisation',
                time: 'Continuous vs. 2-3 hours/week',
                desc: 'AI continuously monitors campaign performance, identifies what\'s working, and automatically adjusts targeting and messaging for better results.',
                color: 'cyan',
                items: ['Real-time ROI tracking across all channels', 'Engagement trend analysis', 'A/B test recommendations', 'Weekly performance reports', 'Actionable optimisation suggestions']
              }
            ].map((item, idx) => (
              <div key={idx} className="relative bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300">
                {/* Faded Micro Grid */}
                <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none opacity-[0.06] overflow-hidden rounded-tl-xl">
                  <div
                    style={{
                      backgroundImage: `linear-gradient(${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : item.color === 'orange' ? '#f97316' : '#06b6d4'} 0.5px, transparent 0.5px),
                                        linear-gradient(90deg, ${item.color === 'blue' ? '#3b82f6' : item.color === 'green' ? '#10b981' : item.color === 'purple' ? '#8b5cf6' : item.color === 'orange' ? '#f97316' : '#06b6d4'} 0.5px, transparent 0.5px)`,
                      backgroundSize: '8px 8px',
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </div>

                <div className="flex items-start gap-4 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-lg flex items-center justify-center font-bold text-lg shadow-sm">
                    {item.num}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 mb-2">
                      <h3 className="text-xl font-bold text-zinc-900">{item.title}</h3>
                      <span className="text-sm text-zinc-500 font-medium">({item.time})</span>
                    </div>
                    <p className="text-zinc-700 mb-4 leading-relaxed">{item.desc}</p>
                    <div className="bg-zinc-50 border border-zinc-200 p-4 rounded-lg">
                      <ul className="text-sm text-zinc-600 space-y-2.5">
                        {item.items.map((feature, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Real Results */}
        <section data-animate="results" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('results') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 size={22} className="text-white" />
            </div>
            Real Results: What 20+ Hours of Freedom Actually Means
          </h2>

          <div className="bg-blue-50 border-2 border-blue-200 p-6 rounded-xl mb-6">
            <h3 className="text-xl font-bold mb-4 text-zinc-900">Time Saved Breakdown</h3>
            <div className="grid md:grid-cols-2 gap-5">
              {[
                { label: 'Content Creation', old: '6-8 hrs', new: '30 min', color: 'blue' },
                { label: 'Scheduling & Posting', old: '3-4 hrs', new: '0 hrs', color: 'green' },
                { label: 'Lead Follow-ups', old: '4-5 hrs', new: '0 hrs', color: 'purple' },
                { label: 'Analytics & Reporting', old: '2-3 hrs', new: '15 min', color: 'orange' }
              ].map((stat, i) => (
                <div key={i} className="bg-white p-5 rounded-lg border border-zinc-200 shadow-sm">
                  <p className="text-sm text-zinc-600 mb-2 font-medium">{stat.label}</p>
                  <p className="text-3xl font-bold text-blue-600">{stat.old} → {stat.new}</p>
                  <p className="text-xs text-zinc-500 mt-2">With AI automation</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-6 bg-blue-600 text-white rounded-xl text-center">
              <p className="text-xl font-bold">Total Time Saved: 20-25 Hours Per Week</p>
              <p className="text-sm text-blue-100 mt-2">That's 80-100 hours per month to focus on strategy and growth</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            {[
              { icon: Target, title: 'Better Strategy', desc: 'Focus on high-impact activities like product development and customer relationships', color: 'blue' },
              { icon: TrendingUp, title: 'Faster Growth', desc: 'Scale marketing efforts 10x without hiring a full team', color: 'green' },
              { icon: Users, title: 'Better Results', desc: 'AI optimisation means higher engagement and conversion rates', color: 'purple' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-zinc-200 p-7 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-300">
                <item.icon className="text-blue-600 mb-4" size={36} />
                <h4 className="font-bold text-xl mb-3 text-zinc-900">{item.title}</h4>
                <p className="text-sm text-zinc-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Getting Started */}
        <section data-animate="started" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('started') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-4 text-zinc-900">
            <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb size={22} className="text-white" />
            </div>
            Getting Started with AI Marketing Automation
          </h2>

          <p className="text-lg text-zinc-700 leading-relaxed mb-10">
            The beauty of modern AI automation is that it's designed for ease of use. Here's how to get started:
          </p>

          <div className="space-y-5 mb-8">
            {[
              { num: '1', title: 'Set Up Your Business Profile', time: '5 minutes', desc: 'Answer a few questions about your business, goals, and target audience. The AI uses this to personalise everything.' },
              { num: '2', title: 'Generate Your Strategy', time: '15 minutes', desc: 'AI creates a comprehensive marketing plan with goals, positioning, and channel strategies tailored to your business.' },
              { num: '3', title: 'Review & Approve Content', time: '30 minutes', desc: 'AI generates hundreds of posts. Review, customise any that need adjustments, and approve for scheduling.' },
              { num: '4', title: 'Set Automation Preferences', time: '10 minutes', desc: 'Configure posting schedules, lead nurturing workflows, and notification preferences.' }
            ].map((step, i) => (
              <div key={i} className="flex gap-5 bg-white border border-zinc-200 p-6 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-300">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-sm">{step.num}</div>
                <div className="flex-1">
                  <div className="flex items-baseline gap-3 mb-2">
                    <h4 className="font-bold text-xl text-zinc-900">{step.title}</h4>
                    <span className="text-sm text-zinc-500 font-medium">({step.time})</span>
                  </div>
                  <p className="text-zinc-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-5 bg-green-50 border-2 border-green-200 p-6 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-2xl shadow-sm">✓</div>
              <div className="flex-1">
                <h4 className="font-bold text-xl mb-2 text-zinc-900">Let AI Handle the Rest</h4>
                <p className="text-zinc-700 leading-relaxed">Sit back and watch as your marketing runs on autopilot. Check in weekly for insights and optimisation suggestions.</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-600 p-8 rounded-r-xl">
            <p className="font-bold text-zinc-900 mb-3 text-lg">Total Setup Time: About 1 Hour</p>
            <p className="text-zinc-700 leading-relaxed">After this one-time setup, you'll save 20+ hours every single week. That's 1,040+ hours per year back in your schedule.</p>
          </div>
        </section>

        {/* Conclusion */}
        <section data-animate="conclusion" className={`mb-12 transition-all duration-1000 ease-out ${
          visibleSections.has('conclusion') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-zinc-900">The Bottom Line</h2>

          <div className="text-zinc-700 leading-relaxed space-y-6">
            <p className="text-lg">
              AI-powered marketing automation isn't about cutting corners or producing lower-quality marketing. It's about <strong className="text-zinc-900">working smarter, scaling faster, and achieving better results</strong> with the limited time and resources you have as a small business.
            </p>

            <p className="text-lg">
              When you're spending 20-25 hours per week on manual marketing tasks, you're not just wasting time—you're missing opportunities to grow your business, serve your customers better, and actually enjoy being an entrepreneur.
            </p>

            <p className="text-lg">
              The businesses winning in 2025 aren't necessarily the ones with the biggest budgets. They're the ones using intelligent automation to punch above their weight, compete with larger competitors, and grow sustainably without burning out.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section data-animate="cta" className={`transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 lg:p-10 rounded-2xl text-center shadow-xl">
            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Reclaim 20+ Hours Per Week?</h3>
            <p className="text-lg text-blue-50 mb-10 max-w-2xl mx-auto leading-relaxed">
              Start your 14-day free trial of Digital Trent and experience intelligent marketing automation designed for small businesses.
            </p>
            <div className="flex flex-col sm:flex-row gap-5 justify-center">
              <button
                onClick={() => navigate('/auth/signup')}
                className="group bg-white text-blue-600 hover:bg-blue-50 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 inline-flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                Start Free Trial
                <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                onClick={() => navigate('/company/contact')}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300"
              >
                See How It Works
              </button>
            </div>
            <p className="text-sm text-blue-50 mt-6">No credit card required • Full access • Cancel any time</p>
          </div>
        </section>
      </article>
    </div>
  );
}
